import { useMemo } from 'react';

import type { ColDef, ColGroupDef } from 'ag-grid-enterprise';

import type { MultisBreakdown } from '@enterprise/api';
import { isNotDefined } from '@enterprise/core';

import type { ListSegmentCellProps } from '@components/grid/renderers/types/list-segment-renderer.params';

import ListSegmentRenderer from '@components/grid/renderers/list-segment-cell-renderer';
import NumberCellRenderer from '@components/grid/renderers/number-cell.renderer';
import TotalCellRenderer from '@components/grid/renderers/total-cell-renderer';

/**
 * Custom hook to generate column definitions for the Multis Breakdown report grid.
 *
 * Returns a memoized array of AG Grid column group definitions, including static columns for mailing information
 * and a dynamic set of columns for multis breakdown based on the provided `dynamicColumnCount`.
 *
 * @param dynamicColumnCount - The number of dynamic "multis breakdown" columns to generate.
 * If not defined or less than 1, only static columns are returned.
 * @returns An array of column group definitions (`ColGroupDef<MultisBreakdown>[]`) for use in AG Grid.
 */
export const useGetMultisBreakdownColumns = (dynamicColumnCount?: number) => {
  return useMemo(() => {
    const columns = [
      {
        children: [
          {
            cellDataType: 'number',
            field: 'mailingId',
            headerClass: ['bg-blue-900'],
            headerName: 'ID',
            hide: true,
            width: 125,
          },
          {
            cellRenderer: TotalCellRenderer,
            field: 'purchaseOrderNumber',
            headerClass: ['bg-blue-900'],
            headerName: 'PO',
            width: 150,
          },
          {
            field: 'mailCode',
            headerClass: ['bg-blue-900'],
            headerName: 'Mail Code',
            width: 150,
          },
          {
            cellRenderer: ListSegmentRenderer,
            cellRendererParams: (params: ListSegmentCellProps<MultisBreakdown>) => {
              return {
                ...params,
                header: 'mailingListDisplayName',
              } as ListSegmentCellProps<MultisBreakdown>;
            },
            field: 'mailingListDisplayName',
            headerClass: ['bg-blue-900'],
            headerName: 'List/Segment',
            width: 300,
            wrapText: true,
          },
          {
            aggFunc: 'sum',
            cellClass: ['bg-green-200', 'text-green-900'],
            cellRenderer: NumberCellRenderer,
            field: 'totalCount',
            filter: 'agNumberColumnFilter',
            headerClass: ['bg-blue-900'],
            headerName: 'Total',
            type: ['numericColumn'],
            width: 150,
          },
        ],
        headerClass: ['bg-blue-950'],
        headerName: 'Mailing Information',
      },
    ] as ColGroupDef<MultisBreakdown>[];

    if (isNotDefined(dynamicColumnCount) || dynamicColumnCount < 1) {
      return columns;
    }

    const children: ColDef<MultisBreakdown>[] = [];

    for (let i = 0; i < dynamicColumnCount; i++) {
      const multi = i + 2;

      children.push({
        aggFunc: 'sum',
        cellClass: ['bg-yellow-200', 'text-yellow-900'],
        cellDataType: 'number',
        cellRenderer: NumberCellRenderer,
        field: `count_multi_${multi}`,
        headerClass: ['bg-yellow-800', 'text-center'],
        headerName: multi.toString(),
        type: ['rightAligned'],
        width: 150,
      });
    }

    columns.push({
      children,
      headerClass: ['bg-yellow-900'],
      headerName: 'Multis Breakdown',
    });

    return columns;
  }, [dynamicColumnCount]);
};
